import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { map, isEmpty, omitBy, isNil } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { encode } from "Helpers/index";
import { useSession } from "Hooks/index";
import { DataTable, Button } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import { BasicModal } from "@life-line-lab/components-react";
import AddToBatchModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/add-to-batch-modal";
import CreateNewBatchModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/create-new-batch";
import deliveryTypeJSON from "Data/delivery-types.json";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { routeToOrderView } from "Helpers/route-order-view";

interface SortingParcelsListProps {
  onFilter?: any;
  customerOptions?: any;
  sortingHubOptions?: any;
  handoverOptions?: any;
  allOrderList?: any;
  onCreateBatch?: any;
  onAddToBatch?: any;
  allBatchesList?: any;
}

const OptionButtons = (props) => {
  const {
    selectedRows,
    handleOpenAddToBatch,
    filterFlag,
    handleOpenCreateNewBatch,
  } = props || {};

  const { t } = useTranslation();
  return (
    <div className="dm-buttons-container">
      <div className="dm-action-btn-container alignment">
        <Button
          type="default"
          isDisabled={isEmpty(selectedRows)}
          label={t("action.addToBatch")}
          size="large"
          onClick={handleOpenAddToBatch}
        />
        <Button
          type="primary"
          label={t("action.createBatch")}
          isDisabled={isEmpty(selectedRows) || filterFlag == false}
          size="large"
          onClick={handleOpenCreateNewBatch}
        />
        <Tooltip title={t("txt.addToBatchGuide")} placement="topLeft">
          <QuestionCircleOutlined className="dm-action-btn-container tool-tip" />
        </Tooltip>
      </div>
    </div>
  );
};

const SortingParcelsList: React.FC<SortingParcelsListProps> = ({
  onFilter,
  customerOptions,
  sortingHubOptions,
  handoverOptions,
  allOrderList,
  onCreateBatch,
  onAddToBatch,
  allBatchesList,
}) => {
  const { roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const initialModal: any = {
    open: false,
  };
  const initialFilter: any = {
    customersList: "",
    sortingHub: "",
    deliveryType: "",
    handoverPoint: "",
  };
  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);

  const initialFilterSelection = [
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
    {
      key: "handoverPoint",
      name: "handoverPoint",
      label: t("label.handoverPoint"),
      placeholder: t("label.handoverPoint"),
      value: filterState?.handoverPoint,
      options: [{ label: t("option.all"), value: "" }].concat(
        handoverOptions?.map((place) => {
          return {
            label: place?.name,
            value: place?.id,
          };
        }),
      ),
    },
    {
      key: "deliveryType",
      name: "deliveryType",
      label: t("label.deliveryType"),
      placeholder: t("placeholder.deliveryType"),
      value: filterState?.deliveryType,
      options: [{ label: t("option.all"), value: "" }].concat(
        map(deliveryTypeJSON.deliveryTypes, (type) => {
          return {
            label: type,
            value: type,
          };
        }),
      ),
    },
    {
      key: "customer",
      name: "customersList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customersList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions?.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
  ];

  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);
  const [filterFlag, setFilterFlag] = useState<any>(false);
  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      setFilterFlag(
        !isEmpty(filterState.sortingHub) && !isEmpty(filterState.deliveryType),
      );
      const filterPayload = omitBy(
        filterState,
        (input) => isNil(input) || isEmpty(input),
      );
      onFilter(filterPayload);
    }
  }, [filterState]);

  useEffect(() => {
    if (
      !isEmpty(sortingHubOptions) ||
      !isEmpty(handoverOptions) ||
      !isEmpty(customerOptions)
    ) {
      setFilterSelection(initialFilterSelection);
    }
  }, [sortingHubOptions, handoverOptions, customerOptions]);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleOpenAddToBatch = () => {
    let orderBatches: any = [];
    allOrderList.map((order) => {
      selectedRows.map((selected) => {
        if (order._id === selected) {
          orderBatches.push(order);
        }
      });
    });
    setCallModal(
      <AddToBatchModal
        allBatchesList={allBatchesList}
        selectedOrders={orderBatches}
        onSubmit={handleAddToBatch}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true, type: "ADD" });
  };

  const handleOpenCreateNewBatch = () => {
    let orderBatches: any = [];
    allOrderList.map((order) => {
      selectedRows.map((selected) => {
        if (order._id === selected) {
          orderBatches.push(order);
        }
      });
    });
    setCallModal(
      <CreateNewBatchModal
        selectedOptions={filterState}
        handoverOptions={sortingHubOptions}
        filterFlag={filterFlag}
        selectedOrdersObj={orderBatches}
        handleCancel={handleResetModalState}
        onSubmit={handleCreateNewBatch}
      />,
    );
    setModal({ open: true, type: "CREATE" });
  };

  const handleOrderView = (orderID) => {
    const params = "module=delivery&tabKey=2";
    routeToOrderView(orderID, location, history, params);
  };

  const [selectedRows, setSelectedRows] = useState<any>([]);

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customersList: selection.customersList,
      sortingHub: selection.sortingHub,
      deliveryType: selection.deliveryType,
      handoverPoint: selection.handoverPoint,
    }));
  };

  const handleCreateNewBatch = (input) => {
    onCreateBatch(input);
    handleResetModalState();
  };

  const handleAddToBatch = (batchParcelID, parcels) => {
    onAddToBatch(batchParcelID, parcels);
    handleResetModalState();
  };

  const handleOpenBatchesView = (batchesID) => {
    let encodedId = encode(batchesID);
    history.push(
      `/delivery-management/parcel-tracking/batches/${encodedId}/view`,
    );
  };

  const splitShipmentColumns: any = [
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.volume").toLocaleUpperCase(),
      dataIndex: ["dimension"],
      width: 70,
      render: (dimension: any) => {
        return (
          <span>
            {dimension?.length} x {dimension?.width} x {dimension?.height}
          </span>
        );
      },
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: ["weight"],
      width: 70,
      render: (weight: any) => {
        return <span>{weight || <span>&#11835;</span>}</span>;
      },
    },
    {
      title: t("label.type").toLocaleUpperCase(),
      dataIndex: ["parcels"],
      width: 80,
      render: (parcels: any) => {
        return (
          <span>
            {map(parcels, (parcel) => {
              return (
                <span>
                  {parcel?.name || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.value").toLocaleUpperCase(),
      dataIndex: ["totalValueOfGoods"],
      width: 70,
      render: (totalValueOfGoods: any) => {
        return (
          <span>
            {parseInt(totalValueOfGoods).toLocaleString() || (
              <span>&#11835;</span>
            )}
            <br />
          </span>
        );
      },
    },
  ];

  const expandable = {
    rowExpandable: (record: any) => record.childrenOrderIDs,
    expandIcon: ({ expanded, onExpand, record }) => {
      if (record.childrenOrderIDs) {
        return expanded ? (
          <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
        ) : (
          <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
        );
      }
    },
    expandedRowRender: (record: any) => {
      return (
        <div className="">
          <DataTable
            width={50}
            columns={splitShipmentColumns}
            dataSource={record.childOrders || []}
          />
        </div>
      );
    },
  };

  const rowSelection =
    roleTypes !== "CUSTOMER-SUPER_ADMIN"
      ? {
          onChange: (selectedRows: any[]) => {
            setSelectedRows(selectedRows);
          },
        }
      : undefined;

  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: ["midMileInfoData", "sortingHub"],
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            {/* <span className="half-square custom-icon" style={{ borderTop: `12px solid transparent` }}>&nbsp;</span> */}
            <span
              className="half-square custom-icon mb-5"
              style={{
                borderLeft: `12px solid transparent`,
                borderBottom: `12px solid #E8A51A`,
              }}
            >
              &nbsp;
            </span>
            <span>
              &nbsp;
              {record.midMileInfoData?.sortingHub?.name || "Something wrong"}
            </span>
          </div>
        );
      },
    },
    {
      title: t("label.batchNo").toLocaleUpperCase(),
      dataIndex: ["midMileInfo", "batchCode"],
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {record.midMileInfo?.batchCode ? (
              <a className="clickable-id">{record.midMileInfo.batchCode}</a>
            ) : (
              <span>{"Not in batch yet"}</span>
            )}
          </span>
        );
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenBatchesView(record.midMileInfoData?.batchCode.id);
          },
        };
      },
    },
    {
      title: t("label.parcelOrderNo").toLocaleUpperCase(),
      dataIndex: "orderCode",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.orderCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOrderView(record._id);
          },
        };
      },
    },
    {
      title: t("label.parcelTrackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.deliveryType").toLocaleUpperCase(),
      dataIndex: ["additionalCharges", "name"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return map(record.additionalCharges, (charges) => {
          if (charges.type == "DELIVERY_TYPE") {
            return <span>{charges.name}</span>;
          }
        });
      },
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "weight",
      width: 105,
      ellipsis: true,
      render: (weight: string) => {
        const roundedWeight = parseFloat(weight).toFixed(2);
        return <span>{roundedWeight}</span>;
      },
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "volume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.volume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: ["senderID", "name"],
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["midMileInfoData", "handoverPoint", "name"],
      width: 105,
      ellipsis: true,
    },
  ];

  return (
    <>
      <div className="parcel-order-list">
        <DataTable
          scroll={{ x: true }}
          rowKey={(record: any) => {
            return record._id;
          }}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={allOrderList || []}
          expandable={expandable}
          title={() => (
            <div>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.sortingSearchbarParcel")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
                {roleTypes == "COMPANY-SUPER_ADMIN" && (
                  <OptionButtons
                    selectedRows={selectedRows}
                    handleOpenAddToBatch={handleOpenAddToBatch}
                    filterFlag={filterFlag}
                    handleOpenCreateNewBatch={handleOpenCreateNewBatch}
                  />
                )}
              </div>
              <div>
                <h4>Total Parcels: {allOrderList.length}</h4>
              </div>
            </div>
          )}
        />
      </div>

      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
        width={modal.type == "ADD" ? "60vw" : "35vw"}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default SortingParcelsList;
